import {
  Avatar,
  Button,
  Input,
  Select,
  Spinner,
  Switch,
  tokens,
} from '@fluentui/react-components';
import {
  Add24Regular,
  ArrowClockwise24Regular,
  ChevronLeft24Regular,
  ChevronRight24Regular,
  CheckmarkCircle24Regular,
  Clock24Regular,
  DismissCircle24Regular,
  ErrorCircle24Regular,
  Prohibited24Regular,
} from '@fluentui/react-icons';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { useHttpClient } from '../../api/HttpClientContext.js';
import { PageError } from '../../components/PageError/PageError.js';
import { useWorkspaceContext } from '../../context/WorkspaceContext.js';
import { useAuth } from '../../hooks/useAuth/useAuth.js';
import { useEvalRuns } from '../../hooks/useEvalRuns/useEvalRuns.js';
import type {
  EvaluationRun,
  RunState,
} from '../../services/workflowApi.interface.js';
import { WorkflowApiService } from '../../services/workflowApi.js';

import { useEvalRunsPageStyles } from './EvalRunsPage.style.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PAGE_SIZE = 25;

// ---------------------------------------------------------------------------
// Status config
// ---------------------------------------------------------------------------

interface StatusConfig {
  label: string;
  color: string;
  bgColor: string;
  icon: React.ReactElement;
}

const STATUS_CONFIG: Record<RunState, StatusConfig> = {
  pending: {
    label: 'Pending',
    color: tokens.colorNeutralForeground3,
    bgColor: tokens.colorNeutralBackground3,
    icon: <Clock24Regular style={{ fontSize: 14 }} />,
  },
  running: {
    label: 'Running',
    color: tokens.colorPaletteBlueForeground2,
    bgColor: tokens.colorPaletteBlueBackground2,
    icon: <ArrowClockwise24Regular style={{ fontSize: 14 }} />,
  },
  completed: {
    label: 'Completed',
    color: tokens.colorPaletteGreenForeground1,
    bgColor: tokens.colorPaletteGreenBackground2,
    icon: <CheckmarkCircle24Regular style={{ fontSize: 14 }} />,
  },
  failed: {
    label: 'Failed',
    color: tokens.colorPaletteRedForeground1,
    bgColor: tokens.colorPaletteRedBackground2,
    icon: <DismissCircle24Regular style={{ fontSize: 14 }} />,
  },
  cancelling: {
    label: 'Cancelling',
    color: tokens.colorPaletteDarkOrangeForeground1,
    bgColor: tokens.colorPaletteDarkOrangeBackground2,
    icon: <ErrorCircle24Regular style={{ fontSize: 14 }} />,
  },
  cancelled: {
    label: 'Cancelled',
    color: tokens.colorNeutralForeground3,
    bgColor: tokens.colorNeutralBackground3,
    icon: <Prohibited24Regular style={{ fontSize: 14 }} />,
  },
};

const ALL_STATUSES = 'All statuses';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ---------------------------------------------------------------------------
// RunRow
// ---------------------------------------------------------------------------

interface RunRowProps {
  run: EvaluationRun;
  workspaceName: string;
}

function RunRow({ run, workspaceName }: RunRowProps) {
  const styles = useEvalRunsPageStyles();
  const navigate = useNavigate();
  const cfg = STATUS_CONFIG[run.runState] ?? STATUS_CONFIG.pending;
  const displayName = run.displayName ?? run.id;
  const createdBy = run.systemProperties.createdBy;
  const createdDate = formatDate(run.systemProperties.createdDate);
  const isTerminal =
    run.runState === 'completed' ||
    run.runState === 'failed' ||
    run.runState === 'cancelled';
  const isSpinning =
    run.runState === 'running' || run.runState === 'cancelling';

  const handleClick = () => {
    if (!isTerminal) return;
    void navigate(
      `/workspaces/${encodeURIComponent(workspaceName)}/evaluation/evaluation-runs/${encodeURIComponent(run.id)}/report`,
    );
  };

  const rowContent = (
    <>
      {/* Left: status badge + name + meta */}
      <div className={styles.rowLeft}>
        <span
          className={styles.statusBadge}
          style={{ color: cfg.color, backgroundColor: cfg.bgColor }}
        >
          <span className={isSpinning ? styles.statusIconSpinning : undefined}>
            {cfg.icon}
          </span>
          {cfg.label}
        </span>
        <div className={styles.rowName}>{displayName}</div>
        <div className={styles.rowMeta}>
          <span>{createdDate}</span>
        </div>
      </div>

      {/* Right: avatar */}
      {createdBy && (
        <div className={styles.rowRight}>
          <Avatar name={createdBy} color="colorful" size={28} />
          <span className={styles.creatorName}>{createdBy}</span>
        </div>
      )}
    </>
  );

  if (isTerminal) {
    return (
      <div
        className={`${styles.row} ${styles.rowClickable}`}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleClick();
        }}
        role="button"
        tabIndex={0}
        aria-label={`View details for ${displayName}`}
      >
        {rowContent}
      </div>
    );
  }

  return (
    <div
      className={`${styles.row} ${styles.rowDisabled}`}
      title="Run is still in progress"
    >
      {rowContent}
    </div>
  );
}

// ---------------------------------------------------------------------------
// EvalRunsPage
// ---------------------------------------------------------------------------

export function EvalRunsPage() {
  const styles = useEvalRunsPageStyles();
  const client = useHttpClient();
  const { workspaceName: workspaceNameParam } = useParams<{
    workspaceName: string;
  }>();
  const workspaceName = workspaceNameParam ?? '';
  const { activeName } = useWorkspaceContext();
  const navigate = useNavigate();
  const { account } = useAuth();

  // When the user switches workspace via the switcher while on this page,
  // redirect to the new workspace's eval-runs URL.
  useEffect(() => {
    if (activeName && workspaceName && activeName !== workspaceName) {
      void navigate(`/workspaces/${encodeURIComponent(activeName)}/eval-runs`, {
        replace: true,
      });
    }
  }, [activeName, workspaceName, navigate]);
  const currentUser = account?.username ?? null;

  // Query-param-driven initial filters. Populated by Trends drill-down
  // (`aistudio:navigate` postMessage → `?date=YYYY-MM-DD&search=<taskName>`)
  // and by any future deep-links from other surfaces. `useSearchParams` is
  // reactive so pasting a filtered URL updates the toolbar in place.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') ?? '';
  const initialDate = searchParams.get('date') ?? '';

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(initialSearch);
  const [dateFilter, setDateFilter] = useState(initialDate);

  // Reset to page 1 when the active workspace changes
  useEffect(() => {
    setPage(1);
  }, [workspaceName]);

  // When the URL params change (e.g. a fresh drill-down from Trends while
  // already on this page), pull them into local filter state.
  useEffect(() => {
    setSearch(initialSearch);
    setDateFilter(initialDate);
    setPage(1);
  }, [initialSearch, initialDate]);

  const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
  const [myRunsOnly, setMyRunsOnly] = useState(false);

  const service = useMemo(() => new WorkflowApiService(client), [client]);
  const { runs, hasNextPage, isLoading, error } = useEvalRuns(
    service,
    workspaceName,
    page,
    PAGE_SIZE,
    statusFilter,
    search,
    dateFilter,
  );

  // Search, status, and date are filtered server-side (see useEvalRuns).
  // The date window is intentionally widened to ±1 UTC-day around the
  // requested drill date to catch long-running evaluations whose
  // `createdDate` straddles midnight relative to the Trends chart's
  // `StartTime` bucket — so we deliberately do NOT re-narrow to a
  // strict day equality here, because that would drop the exact
  // adjacent-day rows the server window is meant to retain. Only the
  // `myRunsOnly` toggle stays client-side (it depends on the signed-in
  // account).
  const filteredRuns = useMemo(
    () =>
      runs.filter((run) => {
        const matchesMine =
          !myRunsOnly ||
          (currentUser != null &&
            run.systemProperties.createdBy === currentUser);
        return matchesMine;
      }),
    [runs, myRunsOnly, currentUser],
  );

  // Trends drill-down previously auto-navigated to the single matching run
  // when the (search + date) filters resolved to exactly one row. The
  // product decision now is to always land on the filtered eval-runs list
  // even when there is a single match, so the user can see the filter
  // context and pick the run explicitly. The auto-nav effect is retained
  // here (commented) so we don't lose the wiring notes if we want to
  // reintroduce a one-click drill later:
  //
  //   if (!isLoading && (initialSearch || initialDate)
  //       && filteredRuns.length === 1) {
  //     void navigate(`.../evaluation-runs/${only.id}/report`, { replace: true });
  //   }

  // Filter mutations keep the URL in sync so refreshes / shares preserve
  // filter state. `replace: true` avoids polluting browser history with a
  // new entry per keystroke.
  const writeParams = (next: {
    search?: string;
    date?: string;
    status?: string;
  }) => {
    const params = new URLSearchParams(searchParams);
    const apply = (key: string, value: string | undefined) => {
      if (value === undefined) return;
      if (value) params.set(key, value);
      else params.delete(key);
    };
    apply('search', next.search);
    apply('date', next.date);
    apply('status', next.status);
    setSearchParams(params, { replace: true });
  };

  // Reset to page 1 when filters change
  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
    writeParams({ search: value });
  };
  const handleStatusChange = (value: string) => {
    setStatusFilter(value);
    setPage(1);
  };
  const handleMyRunsChange = (checked: boolean) => {
    setMyRunsOnly(checked);
    setPage(1);
  };
  const handleClearDate = () => {
    setDateFilter('');
    setPage(1);
    writeParams({ date: '' });
  };

  if (!workspaceName) {
    return (
      <div className={styles.root}>
        <div className={styles.emptyState}>
          Select a workspace to view evaluation runs.
        </div>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.title}>Eval Runs</div>
        <div className={styles.subtitle}>
          Track and launch eval runs for this workspace
        </div>
      </div>

      {error && <PageError error={error} />}

      {/* Toolbar */}
      <div className={styles.toolbar}>
        <div className={styles.toolbarLeft}>
          <Input
            className={styles.searchInput}
            placeholder="Search runs..."
            value={search}
            onChange={(_e, d) => handleSearchChange(d.value)}
          />
          <Select
            value={statusFilter}
            onChange={(_e, d) => handleStatusChange(d.value)}
          >
            <option>{ALL_STATUSES}</option>
            <option>pending</option>
            <option>running</option>
            <option>completed</option>
            <option>failed</option>
            <option>cancelling</option>
            <option>cancelled</option>
          </Select>
          <Switch
            label="My runs only"
            checked={myRunsOnly}
            onChange={(_e, d) => handleMyRunsChange(d.checked)}
          />
          {dateFilter ? (
            <Button
              size="small"
              appearance="outline"
              onClick={handleClearDate}
              aria-label={`Clear date filter ${dateFilter}`}
            >
              Date: {dateFilter} ✕
            </Button>
          ) : null}
        </div>
        <div className={styles.toolbarRight}>
          <Button appearance="primary" icon={<Add24Regular />} disabled>
            Run eval
          </Button>
        </div>
      </div>

      {isLoading ? (
        <Spinner label="Loading runs..." />
      ) : filteredRuns.length === 0 ? (
        <div className={styles.emptyState}>
          {runs.length === 0
            ? 'No evaluation runs found for this workspace.'
            : 'No runs match your current filters.'}
        </div>
      ) : (
        <div className={styles.list}>
          {filteredRuns.map((run) => (
            <RunRow key={run.id} run={run} workspaceName={workspaceName} />
          ))}
        </div>
      )}

      {/* Pagination bar — shown whenever there's a previous or next page */}
      {(page > 1 || hasNextPage) && (
        <div className={styles.pagination}>
          <Button
            appearance="subtle"
            icon={<ChevronLeft24Regular />}
            disabled={page <= 1 || isLoading}
            onClick={() => setPage((p) => p - 1)}
            aria-label="Previous page"
          >
            Previous
          </Button>
          <span className={styles.pageIndicator}>Page {page}</span>
          <Button
            appearance="subtle"
            icon={<ChevronRight24Regular />}
            iconPosition="after"
            disabled={!hasNextPage || isLoading}
            onClick={() => setPage((p) => p + 1)}
            aria-label="Next page"
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
